<template>
  <div class="messaging-actions action-ask-feedback">
    <div class="messaging-actions__content">
      <div class="askfeedback-slider-title">
        <p class="askfeedback-slider-title__principal">
          {{ stepTitle }}
        </p>
        <p class="askfeedback-slider-title__sub">
          {{ stepSubtitle }}
        </p>
      </div>
      <div v-if="actualStep === 1" class="askfeedback-slider">
        <div class="container-slider">
          <input-range
            v-model="rating"
            :min="0"
            :max="4"
            :colors="colorsRange"
          />
        </div>
        <div class="askfeedback-slider__choices">
          <div
            v-for="choice in sliderChoices"
            :key="choice.id"
            class="slider-choice"
            @click="sliderChoiceText(choice.id)"
          >
            <p class="slider-choice__title">
              {{ choice.title }}
            </p>

            <p class="slider-choice__subtitle">
              {{ choice.text }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="actualStep === 2" class="askfeedback-comment">
        <textarea
          v-model="comment"
          :placeholder="feedbackPlaceholder"
          :class="{ error: feedbackLengthError }"
        />
        <div class="action-ask-feedback__counter">
          {{ trans('messaging.actions.ask_feedback.explanation_counter') }}
        </div>
      </div>
      <div v-if="actualStep === 3" class="askfeedback-summary">
        <div class="askfeedback-summary__card">
          <div class="askfeedback-summary-feedback">
            <div class="askfeedback-summary-feedback__slider">
              <p>{{ selectedSliderChoice.title }}</p>
              <p>{{ selectedSliderChoice.text }}</p>
            </div>
            <p v-if="comment" class="askfeedback-summary-feedback__content">
              {{ comment }}
            </p>
          </div>
        </div>
        <div v-if="isUnsatisfiedRating" class="input-checkbox">
          <input
            id="checkbox-archive"
            v-model="checkboxArchive"
            type="checkbox"
          >
          <label
            for="checkbox-archive"
          >{{
            trans('messaging.actions.ask_feedback.checkbox_archive')
          }}</label>
        </div>
      </div>
    </div>
    <div class="messaging-actions__cta-column ask-feedback-ctas">
      <Panel
        v-if="showPanel"
        class="ask-feedback-ctas__panel"
        :type="stepPanelType"
        :icon="stepPanelIcon"
        :title="stepPanelTitle"
        :content="stepPanelContent"
      />
      <button
        v-if="hasNextStep"
        class="btn btn--primary btn--with-icon"
        :disabled="disabledNextStep"
        @click="nextStep()"
      >
        <span>
          {{ trans('global.pagination.next') }}
        </span>
      </button>

      <template v-if="isFinalStep">
        <span class="ctas-sublink" :class="loadingLink ? 'loading-link' : ''" @click="!loading && submit(false)">
          {{
            trans('messaging.actions.ask_feedback.link_summary', {
              company_name: companyName,
            })
          }}
        </span>
        <button class="btn btn--primary" :disabled="loading" :class="loadingBtn ? 'loading-btn' : ''" @click="!loading && submit(true)">
          <span>
            {{
              trans('messaging.actions.ask_feedback.btn_summary', {
                company_name: companyName,
              })
            }}
          </span>
        </button>
      </template>
      <button v-if="toClose" class="btn btn--primary" @click="close()">
        <span>
          {{ trans('global.close') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import InputRange from '@components-molecules/InputRange.vue';
import Panel from '@components-molecules/Panel.vue';
import { useFormAskFeedbackStore } from '@stores/useFormAskFeedbackStore';
import { requestsMixin } from '@tools/requests-mixin';
import { translationMixin } from '@elements/translation';
import { URL_XHR_DEAL_VISIT_FEEDBACK } from '@tools/routes/routes';
import { Logger } from '@tools/logger/logger';
import { eventBus } from '@tools/event-bus';
import { dealMixin } from '@organisms/dealMixin';
import { dealVisitFeedbackMixin } from '@organisms/dealVisitFeedbackMixin';
import { messagingMixins } from '@pages/inbox/mixins';
import { archiveMixins } from './archive-mixins';
import { steps, rating } from './feedback_types';

const FEEDBACK_MAX_LENGTH = 1500;

export default {
  components: {
    InputRange,
    Panel,
  },
  mixins: [ translationMixin, messagingMixins, dealMixin, dealVisitFeedbackMixin, archiveMixins, requestsMixin ],
  props: {
    deal: { type: Object, required: true },
  },
  data() {
    return {
      defaultProgressBar: {
        value: false,
        nbStep: 0,
        actualStep: 0,
      },
      title: this.trans('messaging.actions.ask_feedback.title'),
      feedbackLengthError: false,
      loadingLink: false,
      loadingBtn: false,
      slideValue: 4,
      colorsRange: {
        0: 'danger',
        1: 'danger',
        2: 'warning',
        3: 'success',
        4: 'success',
      },
      response: null,
    };
  },
  computed: {
    store() {
      return useFormAskFeedbackStore();
    },
    actualStep() {
      return this.store.actualStep;
    },
    loading() {
      return this.loadingBtn || this.loadingLink;
    },
    companyName() {
      return this.$companyName;
    },
    showPanel() {
      return (
        !this.feedbackSent || (this.feedbackSent && this.isUnsatisfiedRating)
      );
    },
    isUnsatisfiedRating() {
      return [ rating.NON_COMPLIANT, rating.NOT_SUITABLE ].includes(this.rating);
    },
    isRatingOrComment() {
      return [ steps.RATING, steps.COMMENT ].includes(this.actualStep);
    },
    toClose() {
      return this.feedbackSent && !this.isUnsatisfiedRating;
    },
    isFinalStep() {
      return this.actualStep === this.store.nbStep && !this.feedbackSent;
    },
    hasNextStep() {
      return this.actualStep !== this.store.nbStep && !this.feedbackSent;
    },
    disabledNextStep() {
      return this.feedbackLengthError;
    },
    selectedSliderChoice() {
      return this.sliderChoices.find((c) => c.id === this.rating);
    },
    stepPanelType() {
      if (this.isRatingOrComment) {
        return 'info';
      }
      if (this.actualStep === steps.SUMMARY) {
        return 'orange-lighter';
      }
      return '';
    },
    stepPanelIcon() {
      if (this.isRatingOrComment) {
        return 'ph_lightbulb-fill_blue';
      }
      if (this.actualStep === steps.SUMMARY) {
        return 'ph_shake-hands_orange';
      }
      return '';
    },
    stepPanelTitle() {
      if (!this.feedbackSent) {
        if (this.isRatingOrComment) {
          return this.trans(
            'messaging.actions.ask_feedback.first_step_advice_title',
          );
        }
        if (this.actualStep === steps.SUMMARY) {
          return this.trans(
            'messaging.actions.ask_feedback.third_step_advice_title',
          );
        }
        return '';
      }
      return this.trans(
        'messaging.actions.ask_feedback.third_step_advice_title',
      );
    },
    stepPanelContent() {
      if (!this.feedbackSent) {
        if (this.actualStep === steps.RATING) {
          return this.trans(
            'messaging.actions.ask_feedback.first_step_advice_content',
          );
        }
        if (this.actualStep === steps.COMMENT) {
          return this.trans(
            'messaging.actions.ask_feedback.second_step_advice_content',
          );
        }
        if (this.actualStep === steps.SUMMARY) {
          return this.trans(
            'messaging.actions.ask_feedback.third_step_advice_content',
          );
        }
        return '';
      }
      return this.trans('messaging.actions.ask_feedback.sent_advice');
    },
    // Titre principal en fonction de l'étape du formulaire
    stepTitle() {
      if (this.actualStep === steps.RATING) {
        return this.title;
      }
      if (this.actualStep === steps.COMMENT) {
        return this.trans('messaging.actions.ask_feedback.title_comment');
      }
      if (this.actualStep === steps.SUMMARY) {
        return this.trans('messaging.actions.ask_feedback.title_summary');
      }
      return '';
    },
    // Subtitle according to the stage of the form
    stepSubtitle() {
      if (this.actualStep === steps.RATING) return this.trans('messaging.actions.ask_feedback.advice_slider');
      if (this.actualStep === steps.SUMMARY) return this.trans('messaging.actions.ask_feedback.subtitle_summary');
      return '';
    },
    // Computed value for store update in real time
    progressBarHidden: {
      get() {
        return this.store.progressBarHidden;
      },
      set(value) {
        this.store.progressBarHidden = value;
      },
    },
    feedbackSent: {
      get() {
        return this.store.feedbackSent;
      },
      set(value) {
        this.store.feedbackSent = value;
      },
    },
    checkboxArchive: {
      get() {
        return this.store.checkboxArchive;
      },
      set(value) {
        this.store.checkboxArchive = value;
      },
    },
    rating: {
      get() {
        return this.store.rating;
      },
      set(value) {
        this.store.rating = value;
      },
    },
    comment: {
      get() {
        return this.store.comment;
      },
      set(value) {
        this.store.comment = value;
      },
    },
    feedbackPlaceholder() {
      return this.trans(
        'messaging.actions.ask_feedback.explanation_placeholder',
      );
    },
  },
  watch: {
    comment(newValue) {
      this.feedbackLengthError = newValue.length > FEEDBACK_MAX_LENGTH;
    },
  },
  mounted() {
    eventBus.addEventListener('formstepper-previous', (actualStep) => {
      this.store.changeActualStep(actualStep.detail);
    });
    eventBus.addEventListener('messaging-action-hide', () => {
      this.store.resetValue();
    });
    this.$emit('set-title', this.title);
    this.$emit('set-progressbar', {
      value: true,
      nbStep: this.store.nbStep,
      actualStep: this.actualStep,
    });
  },
  methods: {
    /**
     * Set rating choice when click on text in front of
     * @param {number} id
     */
    sliderChoiceText(id) {
      this.rating = id;
    },
    /**
     * Increments the stepper
     */
    nextStep() {
      this.store.changeActualStep(this.actualStep + 1);
      this.$emit('set-progressbar', {
        value: true,
        nbStep: this.store.nbStep,
        actualStep: this.actualStep,
      });
    },
    /**
     * Send feedback to backend
     * @param {Boolean} shareManager Share feedback to manager
     */
    async submit(shareManager) {
      if (shareManager) {
        this.loadingBtn = true;
      } else {
        this.loadingLink = true;
      }

      const urlDealSaveFeedback = URL_XHR_DEAL_VISIT_FEEDBACK.replace(
        '{id}',
        this.deal.id,
      );

      const textArchive = this.comment.trim() === '' ? `${this.selectedSliderChoice.title} ${this.selectedSliderChoice.text}` : this.comment;

      try {
        this.response = await this.post(
          urlDealSaveFeedback,
          {
            feedback: this.comment,
            rating: this.rating,
            shareManager,
            checkboxArchive: this.checkboxArchive,
            textArchive,
          },
        );
      } catch (error) {
        Logger.error(error);
        this.emitFlashbagGlobalError();
      }

      this.loadingBtn = false;
      this.loadingLink = false;
      if (this.response && this.response.data) {
        this.emitFlashbagSuccessMessage(
          this.trans('messaging.actions.ask_feedback.success_flashbag'),
        );
      }
      this.feedbackSent = true;
      this.progressBarHidden = true;
      this.$emit('hide-progressbar', this.progressBarHidden);
      if (this.checkboxArchive && this.isUnsatisfiedRating) {
        this.toggleArchiveDeal(this.deal.id);
      }
      this.close();
    },
    close() {
      if (this.response) {
        this.$emit('close-slide-dashboard');
        this.$emit('set-progressbar', this.defaultProgressBar);
        this.updateMessagingView(this.response.data);
        this.store.resetValue();
      }
    },
  },
};
</script>
